import './ContentBuilderTemplate.scss';
import './ContentBuilderTemplate102.scss';
import React, { useEffect, useRef } from 'react';
import { usePropsHandler } from '../../EventInterceptor/usePropsHandler';
import { cnContentBuilderTemplate } from './index';
import { Grid, GridItem } from '@dataartdev/uikit/Grid';
import { Typography } from '@dataartdev/uikit/Typography';
import { Slider, SliderItem } from '@dataartdev/uikit/Slider';
import { Picture } from '@dataartdev/uikit/Picture';
import { cn } from '../../../utils/bem';
import { Tooltip } from 'react-tooltip';
import { Swiper } from 'swiper';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { IRoot102 } from './models/CTI_102.props';
import { useSelector } from 'react-redux';
import { screenSelector } from '../../../../store';

const cnAdvantagesCarousel = cn('AdvantagesCarousel');
const cnAdvantageSlide = cn('AdvantageSlide');
const cnAdvantagesCarouselPagination = cn('AdvantagesCarouselPagination');
const cnAdvantagesCarouselButtons = cn('AdvantagesCarouselButtons');

interface SwiperRef {
  swiper: Swiper;
}

export const ContentBuilderTemplate: React.FC<IRoot102> = props => {
  const { type, items, title, contentId } = usePropsHandler(
    cnContentBuilderTemplate(),
    props
  );

  const typeNumber = type.toString();

  const advantagesCarouselConfig = {
    watchOverflow: false,
    navigation: {
      nextEl: '#nextSlide',
      prevEl: '#prewSlide',
    },
    pagination: {
      el: '.AdvantagesCarouselPagination',
      clickable: true,
      bulletClass: 'AdvantagesCarouselPagination-Item',
      bulletActiveClass: 'AdvantagesCarouselPagination-Item_active',
    },
    slidesPerView: 1,
    spaceBetween: 0,
    loop: true,
    watchSlidesProgress: true,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
      // pauseOnMouseEnter: true,
    },
  };

  const sliderRef = useRef<SwiperRef>(null);

  const [isVisible] = useIntersectionObserver(sliderRef, {}, 0);
  useEffect(() => {
    const swiperInstance = sliderRef?.current?.swiper;

    const handleMouseEnter = () => {
      swiperInstance?.autoplay?.stop();
      swiperInstance?.el.classList.add('swiper-paused');
      const activeNavItem = swiperInstance?.el?.querySelector(
        '.AdvantagesCarouselPagination-Item_active'
      );
      // if (activeNavItem) {
      //   activeNavItem.style.animationPlayState = "paused";
      // }
    };

    const handleMouseLeave = () => {
      swiperInstance?.autoplay?.start();
      swiperInstance?.el.classList.remove('swiper-paused');
      const activeNavItem = swiperInstance?.el.querySelector(
        '.AdvantagesCarouselPagination-Item_active'
      );
      activeNavItem?.classList.remove(
        'AdvantagesCarouselPagination-Item_active'
      );

      setTimeout(() => {
        activeNavItem?.classList.add(
          'AdvantagesCarouselPagination-Item_active'
        );
      }, 10);
    };

    if (isVisible) {
      handleMouseLeave();
    } else {
      handleMouseEnter();
    }

    if (swiperInstance && swiperInstance?.el) {
      swiperInstance?.el?.addEventListener('mouseenter', handleMouseEnter);
      swiperInstance?.el?.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (swiperInstance && swiperInstance?.el) {
        swiperInstance?.el?.removeEventListener('mouseenter', handleMouseEnter);
        swiperInstance?.el?.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, [isVisible]);

  const { isMobile } = useSelector(screenSelector);

  return (
    <div
      className={cnContentBuilderTemplate({ type: typeNumber })}
      id={contentId}
    >
      <Grid
        breakpoints={{
          desktop: { cols: 12, gap: 'xl' },
          tablet: { cols: 12, gap: 'xl' },
          mobile: { cols: 12, gap: 'xl' },
        }}
        className={cnAdvantagesCarousel()}
      >
        <GridItem
          breakpoints={{
            desktop: { col: 10, colStart: 2 },
            tablet: { col: 10 },
            mobile: { col: 12 },
          }}
        >
          {title && (
            <Typography.Title as="h2" size="lg" parse>
              {title}
            </Typography.Title>
          )}
        </GridItem>
        <GridItem
          breakpoints={{
            desktop: { col: 10, colStart: 2 },
            tablet: { col: 12 },
            mobile: { col: 12 },
          }}
        >
          <div className={cnAdvantagesCarousel('Inner')}>
            <Slider
              ref={sliderRef}
              {...advantagesCarouselConfig}
              className={cnAdvantagesCarousel('Slider')}
            >
              <div className={cnAdvantagesCarouselPagination()}></div>
              {items &&
                items.length > 0 &&
                items.map(item => {
                  const { id, title, text, items: tooltips, picture } =
                    item || {};
                  return (
                    <SliderItem key={id}>
                      <div className={cnAdvantageSlide()}>
                        <div className={cnAdvantageSlide('Left')}>
                          {title && (
                            <Typography.Title
                              parse
                              as="h3"
                              size="sm"
                              className={cnAdvantageSlide('Title')}
                            >
                              {title}
                            </Typography.Title>
                          )}
                          {text && (
                            <Typography.Text
                              parse
                              as="p"
                              size="l"
                              className={cnAdvantageSlide('Text')}
                            >
                              {text}
                            </Typography.Text>
                          )}
                        </div>
                        <div className={cnAdvantageSlide('Image')}>
                          {tooltips &&
                            tooltips.length > 0 &&
                            tooltips.map(tooltip => {
                              const { id, text, yAxis, xAxis } = tooltip || {};
                              if (!text) return null;
                              return (
                                <div
                                  key={id}
                                  className={cnAdvantageSlide('Tooltip')}
                                  style={{
                                    left: `${xAxis}%`,
                                    top: `${yAxis}%`,
                                  }}
                                >
                                  <div
                                    className={cnAdvantageSlide(
                                      'Tooltip-Trigger'
                                    )}
                                    data-tooltip-id={tooltip.id}
                                    data-tooltip-html={text}
                                  />
                                  <Tooltip
                                    className={cnAdvantageSlide(
                                      'Tooltip-Inner'
                                    )}
                                    id={tooltip.id}
                                    openOnClick={isMobile}
                                  />
                                </div>
                              );
                            })}
                          {picture && <Picture {...picture} />}
                        </div>
                      </div>
                    </SliderItem>
                  );
                })}
            </Slider>
            {items && items.length > 1 && (
              <div className={cnAdvantagesCarouselButtons()}>
                <button
                  className={cnAdvantagesCarouselButtons('Button')}
                  id="prewSlide"
                />
                <button
                  className={cnAdvantagesCarouselButtons('Button')}
                  id="nextSlide"
                />
              </div>
            )}
          </div>
        </GridItem>
      </Grid>
    </div>
  );
};

export default ContentBuilderTemplate;
